/**
 * EDITOR DEL FILTRO DELLA BOARD (TS-102) — con quali record la bacheca si riempie,
 * fra quelli della sua pipeline.
 *
 * TS-117: non sa piu' di che TIPO siano. Campi e ambiti arrivano dal catalogo del contributore
 * del tipo della bacheca, e qui dentro non c'e' nessun valore cablato — nessun 'QUOTATION' di
 * partenza, nessuna etichetta ricostruita da una chiave i18n. Catalogo vuoto e' una risposta
 * legittima: l'editor non offre nulla e lo dice.
 *
 * GEMELLO: a vista è il blocco SE del builder automazioni (`AutomationForm.tsx`, sezione
 * `builder-kw--if`). Il codice è indipendente — così si è deciso — e la parità la tiene il
 * FOGLIO DI STILE: le classi `builder-*` sono le stesse e nessuna è duplicata qui. Chi
 * tocca la resa di una delle due guardi anche l'altra, o divergono in silenzio.
 *
 * `ChipSelect` invece si importa da `entities/automation/catalogFieldEditors`: è una combo
 * resa a pastiglia, non dominio automazioni, e riscriverla sarebbe stata la stessa cosa due
 * volte. Vive lì perché lì è nata; se un terzo consumatore la chiama, si sposta in
 * `components/`.
 *
 * Campi, operatori e valori ammessi arrivano dal catalogo del backend già localizzati:
 * l'editor non può offrire una condizione che il server poi rifiuta.
 *
 * Fuori perimetro: il termine destro "un altro campo del record" delle automazioni. Solo
 * valori statici.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipSelect, type ChipOption } from '../automation/catalogFieldEditors';
import type {
  KanbanFilter,
  KanbanFilterCondition,
  KanbanFilterField,
  KanbanFilterOperator,
  KanbanFilterScope,
} from './kanbanMappers';

/** Lo scope da solo non identifica un campo: la chiave del chip è la coppia. */
const keyOf = (scope: KanbanFilterScope, fieldName: string) => `${scope}:${fieldName}`;

/**
 * Condizione appena aggiunta: senza campo, quindi né operatore né valore da disegnare.
 *
 * Lo scope parte VUOTO e non da 'QUOTATION': il catalogo è del tipo della board, e un default
 * cablato sul preventivo sarebbe uno scope che per una board di task non esiste. Lo valorizza la
 * scelta del campo, che porta con sé il proprio ambito.
 */
const EMPTY: KanbanFilterCondition = {
  scope: '', fieldName: '', operator: 'EQ', values: [],
};

export function KanbanFilterEditor({
  filter,
  fields,
  onChange,
  disabled = false,
}: {
  filter: KanbanFilter | null;
  /** Catalogo dal backend. Vuoto finché non arriva: l'editor lo dice e non offre nulla. */
  fields: KanbanFilterField[];
  onChange: (filter: KanbanFilter | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const groups = filter?.groups ?? [];

  // Menu piatto, l'ambito nel prefisso: senza, "Stato" comparirebbe tre volte identico.
  const fieldOptions: ChipOption[] = useMemo(
    () => fields.map((f) => ({
      value: keyOf(f.scope, f.fieldName),
      label: `${f.scopeLabel} · ${f.label}`,
    })),
    [fields],
  );

  // Un filtro svuotato esce come `null`: "nessun filtro" deve avere una forma sola.
  const emit = (next: KanbanFilter['groups']) => onChange(next.length ? { groups: next } : null);

  const setConditions = (gi: number, conditions: KanbanFilterCondition[]) =>
    conditions.length
      ? emit(groups.map((g, i) => (i === gi ? { conditions } : g)))
      // Un gruppo senza condizioni il backend lo rifiuta, e nessuno vuole restare con un
      // gruppo vuoto in pagina: togliere l'ultima condizione toglie il gruppo.
      : emit(groups.filter((_, i) => i !== gi));

  return (
    <section className="builder-card">
      <div className="builder-card__head">
        <span className="builder-kw builder-kw--if">{t('kanban.filter.title')}</span>
        <span className="builder-card__hint">{t('kanban.filter.hint')}</span>
      </div>
      <div className="builder-card__body">
        {!fields.length ? (
          <span className="builder-card__hint">{t('kanban.filter.noCatalog')}</span>
        ) : (
          <>
            {!groups.length && (
              // Una board senza filtro non è incompleta, è il caso normale: lo si dice.
              <span className="builder-card__hint">{t('kanban.filter.empty')}</span>
            )}
            {groups.map((group, gi) => (
              // width:100% — il body è flex-wrap, senza il wrapper i gruppi si affiancano.
              <div key={gi} className="builder-cgroup-wrap">
                {gi > 0 && <div className="builder-or">{t('kanban.filter.logicOr')}</div>}
                <div className="builder-cgroup">
                  <div className="builder-glabel">
                    <span>{t('kanban.filter.allTrue')}</span>
                    <button
                      type="button"
                      className="builder-crow__remove"
                      aria-label={t('kanban.filter.removeGroup')}
                      disabled={disabled}
                      onClick={() => emit(groups.filter((_, i) => i !== gi))}
                    >
                      ✕
                    </button>
                  </div>
                  {group.conditions.map((condition, ci) => (
                    <ConditionRow
                      key={ci}
                      condition={condition}
                      fields={fields}
                      fieldOptions={fieldOptions}
                      showLogic={ci > 0}
                      disabled={disabled}
                      onChange={(next) =>
                        setConditions(gi, group.conditions.map((c, i) => (i === ci ? next : c)))}
                      onRemove={() =>
                        setConditions(gi, group.conditions.filter((_, i) => i !== ci))}
                    />
                  ))}
                  {!disabled && (
                    <a className="builder-add"
                      onClick={() => setConditions(gi, [...group.conditions, EMPTY])}>
                      ＋ {t('kanban.filter.addCondition')}
                    </a>
                  )}
                </div>
              </div>
            ))}
            {!disabled && (
              <a className="builder-add builder-add--group"
                onClick={() => emit([...groups, { conditions: [EMPTY] }])}>
                ＋ {t('kanban.filter.addGroup')}
              </a>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** Riga condizione: si legge come una frase, ed è perché i controlli sono pastiglie. */
function ConditionRow({
  condition, fields, fieldOptions, showLogic, disabled, onChange, onRemove,
}: {
  condition: KanbanFilterCondition;
  fields: KanbanFilterField[];
  fieldOptions: ChipOption[];
  showLogic: boolean;
  disabled: boolean;
  onChange: (condition: KanbanFilterCondition) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const field = fields.find(
    (f) => f.scope === condition.scope && f.fieldName === condition.fieldName);
  const operator = field?.operators.find((o) => o.code === condition.operator);

  const handleField = (key: string) => {
    // Scope e nome si riprendono DAL CATALOGO e non si spacchetta la chiave: gli scope non sono
    // piu' i tre del core, li dichiara il contributore del tipo, e un ':' dentro il nome di uno
    // scope di un plugin taglierebbe la condizione a meta' senza che nessuno se ne accorga.
    const next = fields.find((f) => keyOf(f.scope, f.fieldName) === key);
    if (!next) return;

    // Campo nuovo = altro tipo, quindi altri operatori e altra forma del valore: si azzera,
    // o resterebbe una condizione che il backend rifiuterebbe.
    onChange({
      scope: next.scope,
      fieldName: next.fieldName,
      operator: next.operators[0]?.code ?? 'EQ',
      values: [],
    });
  };

  const handleOperator = (code: string) => {
    const next = field?.operators.find((o) => o.code === code);
    // I valori si tengono se la cardinalità non cambia: passare da "maggiore" a "minore"
    // non deve far riscrivere il numero.
    onChange({
      ...condition,
      operator: code as KanbanFilterOperator,
      values: next?.cardinality === operator?.cardinality ? condition.values : [],
    });
  };

  const setValue = (i: number, value: string) => {
    const values = [...condition.values];
    values[i] = value;
    onChange({ ...condition, values });
  };

  return (
    <div className="builder-crow">
      {showLogic && <span className="builder-logic">{t('kanban.filter.logicAnd')}</span>}
      <ChipSelect
        value={condition.fieldName ? keyOf(condition.scope, condition.fieldName) : ''}
        options={fieldOptions}
        placeholder={t('kanban.filter.selectField')}
        ariaLabel={t('kanban.filter.selectField')}
        disabled={disabled}
        onChange={handleField}
      />
      {field && (
        <>
          <ChipSelect
            className="builder-op"
            value={condition.operator}
            options={field.operators.map((o) => ({ value: o.code, label: o.label }))}
            placeholder={t('kanban.filter.selectOperator')}
            ariaLabel={t('kanban.filter.selectOperator')}
            disabled={disabled}
            onChange={handleOperator}
          />
          <ConditionValue
            field={field}
            cardinality={operator?.cardinality}
            values={condition.values}
            disabled={disabled}
            onSetValue={setValue}
            onSetValues={(values) => onChange({ ...condition, values })}
          />
        </>
      )}
      <button
        type="button"
        className="builder-crow__remove"
        aria-label={t('common.delete')}
        disabled={disabled}
        onClick={onRemove}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Termine destro: il TIPO del campo dice che controllo disegnare, la CARDINALITÀ
 * dell'operatore quanti. I valori restano stringhe anche per numeri e date — è il contratto
 * REST, e convertirli qui vorrebbe dire riconvertirli prima di inviarli.
 */
function ConditionValue({
  field, cardinality, values, disabled, onSetValue, onSetValues,
}: {
  field: KanbanFilterField;
  cardinality: string | undefined;
  values: string[];
  disabled: boolean;
  onSetValue: (index: number, value: string) => void;
  onSetValues: (values: string[]) => void;
}) {
  const { t } = useTranslation();

  if (field.type === 'ENUM') {
    const options = (field.enumValues ?? []).map((v) => ({ value: v.value, label: v.label }));
    // SET (operatore IN): una pastiglia per valore più una per aggiungerne. Nessun controllo
    // nuovo, così la riga resta una frase anche con cinque valori.
    const chips = cardinality === 'SET' ? values : values.slice(0, 1);

    return (
      <>
        {chips.map((v, i) => (
          <ChipSelect
            key={i}
            className="builder-selchip builder-selchip--val"
            value={v}
            options={options}
            ariaLabel={t('kanban.filter.selectValue')}
            disabled={disabled}
            onChange={(next) => onSetValue(i, next)}
          />
        ))}
        {(cardinality === 'SET' || !values.length) && (
          <ChipSelect
            className="builder-selchip builder-selchip--val"
            value=""
            options={options}
            placeholder={t(values.length ? 'kanban.filter.addValue' : 'kanban.filter.selectValue')}
            ariaLabel={t('kanban.filter.selectValue')}
            disabled={disabled}
            onChange={(next) => onSetValues([...values, next])}
          />
        )}
      </>
    );
  }

  const numeric = field.type === 'INTEGER' || field.type === 'DECIMAL' || field.type === 'MONEY';
  const inputType = field.type === 'DATE' ? 'date' : numeric ? 'number' : 'text';
  // Il passo dice al browser quali numeri sono validi: interi per INTEGER, i centesimi per MONEY
  // (come il builder delle automazioni), qualunque decimale per DECIMAL. Non è solo estetica: in
  // creazione l'editor sta dentro un <form>, e senza passo un importo con i centesimi bloccava
  // l'invio, perché il passo predefinito di un campo numerico è 1.
  const step =
    field.type === 'INTEGER' ? '1' : field.type === 'MONEY' ? '0.01' :
      field.type === 'DECIMAL' ? 'any' : undefined;

  return (
    <>
      {Array.from({ length: cardinality === 'RANGE' ? 2 : 1 }, (_, i) => (
        <span className="builder-fieldnum" key={i}>
          <input
            type={inputType}
            step={step}
            value={values[i] ?? ''}
            disabled={disabled}
            aria-label={t(cardinality === 'RANGE'
              ? (i === 0 ? 'kanban.filter.valueFrom' : 'kanban.filter.valueTo')
              : 'kanban.filter.selectValue')}
            onChange={(e) => onSetValue(i, e.target.value)}
          />
        </span>
      ))}
    </>
  );
}
